import React from 'react';
import Button from './ui/Button';
import useParallax from '../hooks/useParallax';
import photo from '../lib/images';
import { hero } from '../data/content';

/**
 * The opening frame. The photograph does two things at once: a very slow
 * push-in that runs on load, and a drift against the scroll — so the layer is
 * split in two, since a JS transform on the image would overwrite the
 * CSS animation.
 */
export default function Hero() {
  const parallaxRef = useParallax({ strength: 0.18, scale: 1.18 });

  // Each line enters on its own beat rather than the block arriving at once.
  const enter = (i) => ({
    animation: 'fade-up 1.1s cubic-bezier(0.16, 1, 0.3, 1) both',
    animationDelay: `${180 + i * 130}ms`,
  });

  return (
    <section
      id="top"
      className="on-dark grain min-h-hero relative isolate flex items-center overflow-hidden"
    >
      <div ref={parallaxRef} className="absolute inset-0 -z-20 will-change-transform">
        <img
          {...photo(hero.image)}
          sizes="100vw"
          alt="Two men walking a dirt road together at sunrise"
          className="h-full w-full origin-center animate-kenburns object-cover object-[68%_center]"
          fetchpriority="high"
          decoding="async"
        />
      </div>

      {/* Three overlays: an overall darkening, a column to hold the copy
          readable over the sunrise, and a vignette to settle the edges.
          The column stops are written out because the copy runs to roughly
          40% of the width, right where the sun is brightest. */}
      <div className="absolute inset-0 -z-10 bg-ink-900/35" aria-hidden="true" />
      <div
        className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(10,10,12,0.95)_0%,rgba(10,10,12,0.86)_55%,rgba(10,10,12,0.62)_100%)] lg:bg-[linear-gradient(90deg,rgba(10,10,12,0.96)_0%,rgba(10,10,12,0.88)_26%,rgba(10,10,12,0.6)_44%,rgba(10,10,12,0)_72%)]"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(120%_100%_at_50%_50%,transparent_35%,rgba(10,10,12,0.55)_100%)]"
        aria-hidden="true"
      />

      <div className="shell relative w-full pb-24 pt-36 sm:pb-28 lg:py-32">
        <div className="max-w-xl lg:max-w-2xl">
          <p
            style={enter(0)}
            className="font-display text-2xl font-semibold uppercase tracking-[0.02em] text-gold sm:text-3xl lg:text-[2.5rem]"
          >
            {hero.eyebrow}
          </p>

          <h1
            style={enter(1)}
            className="mt-2 font-display text-[2.6rem] font-semibold uppercase leading-[0.98] tracking-[0.005em] text-white sm:text-5xl lg:text-[4.5rem]"
          >
            {hero.title}
          </h1>

          <span style={enter(2)} className="rule mt-8" />

          <p
            style={enter(3)}
            className="mt-6 font-display text-sm font-medium uppercase tracking-[0.26em] text-gold sm:text-base"
          >
            {hero.tagline}
          </p>

          <p style={enter(4)} className="mt-5 max-w-md text-[0.98rem] leading-relaxed text-cream/80 sm:text-base">
            {hero.body}
          </p>

          <div style={enter(5)} className="mt-10">
            <Button href={hero.cta.href} size="lg" arrow>
              {hero.cta.label}
            </Button>
          </div>
        </div>
      </div>

      <div
        className="absolute inset-x-0 bottom-8 hidden justify-center lg:flex"
        style={{ animation: 'fade-up 1.1s cubic-bezier(0.16, 1, 0.3, 1) both', animationDelay: '1.1s' }}
        aria-hidden="true"
      >
        <span className="h-14 w-px overflow-hidden bg-cream/15">
          <span className="block h-full w-full animate-scroll-cue bg-gold" />
        </span>
      </div>
    </section>
  );
}
